import { backendApiClient } from '@geneo2-web/services-clients';
import { PAL_BASE_URL } from '@geneo2-web/shared-ui';
import { AxiosRequestConfig } from 'axios';
import {
  AnswerHintUsedEnum,
  AnswerModeEnum,
  SubmissionDetails,
} from './common';
import type { IPerformanceSummary } from './learners';

const START_DIAGNOSTIC_PATH = (session_id: string) =>
  `${
    PAL_BASE_URL || ''
  }/adaptive/diagnostic/sessions/${session_id}/start-diagnostic`;

export interface IStartDiagnosticRequest {
  cross_module_depth?: number;
  max_questions?: number;
}

export interface IDiagnosticFirstQuestion {
  question_id: string;
  bloom_level: string;
  question_bloom: string;
  difficulty: string;
  difficulty_level: string;
  primary_concept_id: string;
  primary_concept_name: string;
  selection_reason: string;
  info_gain: number;
  source_module_id: string;
  source_question_id: string;
}

export interface IDiagnosticConfigSnapshot {
  diagnostic_initial_theta: number;
  diagnostic_knows_threshold: number;
  diagnostic_gap_threshold: number;
  diagnostic_theta_clamp_min: number;
  diagnostic_theta_clamp_max: number;
  diagnostic_cross_module_depth: number;
  diagnostic_max_questions: number;
}

export interface IDiagnosticGraphMeta {
  total_concepts: number;
  total_dependencies: number;
  max_depth: number;
  is_valid_dag: boolean;
}

export interface IStartDiagnosticData {
  session_id: string;
  first_question: IDiagnosticFirstQuestion;
  initial_thetas: Record<string, number>;
  config_snapshot: IDiagnosticConfigSnapshot;
  graph_meta: IDiagnosticGraphMeta;
  target_module_concept_ids: string[];
  cross_module_concept_ids: string[];
  message: string;
}

export interface IStartDiagnosticResponse {
  question_mapping: Record<string, string>;
  data: IStartDiagnosticData;
}

export async function startDiagnosticApi(
  session_id: string,
  body: IStartDiagnosticRequest = {},
  options: AxiosRequestConfig = {}
): Promise<IStartDiagnosticResponse> {
  return await backendApiClient.post<IStartDiagnosticResponse>(
    START_DIAGNOSTIC_PATH(session_id),
    body,
    options
  );
}

const SUBMIT_DIAGNOSTIC_ANSWER_PATH = (session_id: string) =>
  `${
    PAL_BASE_URL || ''
  }/adaptive/diagnostic/sessions/${session_id}/diagnostic/answer`;

export interface ISubmitDiagnosticAnswerRequest {
  question_id: string;
  answer: string[];
  time_taken_seconds: number;
  answer_mode: AnswerModeEnum;
  hint_used?: AnswerHintUsedEnum;
  primary_concept_id?: string;
}

export interface IDiagnosticThetaUpdate {
  concept_id: string;
  concept_name: string;
  theta_before: number;
  theta_after: number;
  delta: number;
  update_type: string;
}

export interface IDiagnosticProgress {
  questions_answered: number;
  max_questions: number;
  concepts_classified: number;
  total_concepts: number;
}

export interface IDiagnosticClassificationSummary {
  knows: number;
  uncertain: number;
  gap: number;
}

export interface ISubmitDiagnosticAnswerResponse extends SubmissionDetails {
  result: string;
  score_value: number;
  theta_updates: IDiagnosticThetaUpdate[];
  next_question?: IDiagnosticFirstQuestion;
  progress: IDiagnosticProgress;
  classification_summary: IDiagnosticClassificationSummary;
  is_complete: boolean;
  stop_reason?: string;
}

export async function submitDiagnosticAnswerApi(
  session_id: string,
  body: ISubmitDiagnosticAnswerRequest,
  options: AxiosRequestConfig = {}
): Promise<ISubmitDiagnosticAnswerResponse> {
  return await backendApiClient.post<ISubmitDiagnosticAnswerResponse>(
    SUBMIT_DIAGNOSTIC_ANSWER_PATH(session_id),
    body,
    options
  );
}

const APPLY_DIAGNOSTIC_TO_PAL_PATH = (session_id: string) =>
  `${
    PAL_BASE_URL || ''
  }/adaptive/diagnostic/sessions/${session_id}/diagnostic/apply-to-pal`;

export interface IApplyDiagnosticToPalRequest {
  merge_strategy?: 'initialize_only' | 'override';
}

export interface IApplyDiagnosticThetaMapping {
  concept_id: string;
  concept_name: string;
  diagnostic_theta: number;
  diagnostic_classification: string;
  pal_theta: number;
  pal_readiness: number;
  was_initialized: boolean;
}

export interface IApplyDiagnosticToPalResponse {
  session_id: string;
  applied_at: string;
  merge_strategy: string;
  concepts_initialized: number;
  concepts_skipped: number;
  theta_mappings: IApplyDiagnosticThetaMapping[];
  message: string;
}

export async function applyDiagnosticToPalApi(
  session_id: string,
  body: IApplyDiagnosticToPalRequest,
  options: AxiosRequestConfig = {}
): Promise<IApplyDiagnosticToPalResponse> {
  return await backendApiClient.post<IApplyDiagnosticToPalResponse>(
    APPLY_DIAGNOSTIC_TO_PAL_PATH(session_id),
    body,
    options
  );
}

const DIAGNOSTIC_RESULTS_PATH = (session_id: string) =>
  `${
    PAL_BASE_URL || ''
  }/adaptive/diagnostic/sessions/${session_id}/diagnostic/results`;

export interface IDiagnosticResultData {
  summary_text?: string;
}

export interface IDiagnosticResultsResponse {
  results: IPerformanceSummary;
  data?: IDiagnosticResultData;
}

export async function getDiagnosticResultsApi(
  session_id: string,
  module_id: string,
  options: AxiosRequestConfig = {}
): Promise<IDiagnosticResultsResponse> {
  return await backendApiClient.get<IDiagnosticResultsResponse>(
    DIAGNOSTIC_RESULTS_PATH(session_id),
    { ...options, params: { module_id, ...options.params } }
  );
}
